import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { ILoggerService, ILogService } from "../../log/common/log.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { ICommandService } from "../../commands/common/commands.js";
import { IKeyboardService, IKeyboardEventWithSource } from "../../keyboard/common/keyboardService.js";
import { IKeyboardEvent, StandardKeyboardEvent } from "../../../base/browser/keyboardEvent.js";
import { isEditableElementFocused } from "../../../base/browser/dom.js";
import { selectionForElement } from "../../../base/browser/selection.js";
import { IUserConfiguration } from "../../../editor/services/user-configuration/common/userConfiguration.js";
import { ContextKeyExpr, IContextKey, IContextKeyService, type ContextKeyValue } from "../../contextkey/common/contextkey.js";
import { ResolvedKeybinding } from "../../../base/common/keybindings.js";
import { USLayoutResolvedKeybinding } from "./usLayoutResolvedKeybinding.js";
import { OperatingSystem, OS } from "../../../base/common/platform.js";

/**
 * Represents a keyboard key combination.
 * Examples: "Ctrl+Z", "Shift+Alt+F", "Delete", "Escape"
 */
export type KeyCombo = string;

/**
 * A keybinding maps a key combination to a command with an optional context condition.
 */
export interface IKeybinding {
	/**
	 * Unique identifier for this keybinding
	 */
	readonly id: string;

	/**
	 * The key combination that triggers this binding
	 * Examples: "Ctrl+Z", "Delete", "Shift+Alt+F"
	 */
	readonly key: KeyCombo | KeyCombo[];

	/** Optional physical KeyboardEvent.code match for layout-independent panel keys. */
	readonly code?: string | string[];

	/**
	 * The command ID to execute when this keybinding is triggered
	 */
	readonly commandId: string;

	/**
	 * Optional context condition - when clause that must be true for the keybinding to be active
	 * Examples: "treeViewFocus", "!textInputFocus", "treeViewFocus && !modalOpen"
	 */
	readonly when?: string;

	/**
	 * Arguments to pass to the command when executed
	 */
	readonly args?: any;

	preventDefault?: boolean;

	/**
	 * Chrome extension: also deliver this binding while the HOST page has focus
	 * (keybindings are normally scoped to the editor UI — see
	 * `scopeEventsToRegisteredRoots`). Gated by the "canvas shortcuts" user
	 * setting and skipped while an editable element is focused; when it fires,
	 * the key is SWALLOWED from the host (it must not also drive the page).
	 * Reserve this for edit-loop shortcuts (undo/redo, gizmo modes) — every
	 * flagged binding is a key the inspected site loses while it applies.
	 */
	readonly allowOnCanvas?: boolean;
}

export interface IResolvedKeybinding {
	getLabel(): string;
}

/**
 * Service for managing keybindings and keyboard shortcuts.
 *
 * Listens to keyboard events and executes commands when keybindings are triggered.
 * Supports context-aware keybindings using "when" clauses.
 */
export interface IKeybindingService {
	/**
	 * Event fired when a keybinding is added or removed
	 */
	readonly onDidUpdateKeybindings: Event<void>;

	/**
	 * Register a keybinding
	 * @returns Disposable to unregister the keybinding
	 */
	registerKeybinding(binding: IKeybinding): IDisposable;

	/**
	 * Get all registered keybindings
	 */
	getKeybindings(): readonly IKeybinding[];

	/**
	 * Look up the keybinding for a command, optionally considering context
	 */
	lookupKeybinding(commandId: string, context?: IContextKeyService, enforceContextCheck?: boolean): IResolvedKeybinding | undefined;

	/**
	 * Get keybindings for a specific command
	 */
	getKeybindingsForCommand(commandId: string): readonly IKeybinding[];


	/**
	 * Resolve which keybinding (if any) would be triggered by the given keyboard event
	 */
	resolveKeybinding(event: KeyboardEvent): IKeybinding | undefined;

	/** Create a context key in the same context evaluated by `when` clauses. */
	createContextKey<T extends ContextKeyValue>(key: string, defaultValue: T): IContextKey<T>;

	/** Evaluate a when-clause against the shared shortcut context. */
	contextMatches(when: string): boolean;
}



// #region Implementation

export const IKeybindingService = createDecorator<IKeybindingService>('keybindingService');

export class KeybindingService extends Disposable implements IKeybindingService {
	declare readonly _serviceBrand: undefined;

	private readonly _onDidUpdateKeybindings = this._register(new Emitter<void>());
	readonly onDidUpdateKeybindings = this._onDidUpdateKeybindings.event;

	private readonly keybindings = new Map<string, IKeybinding>();
	private readonly handledEvents = new WeakSet<KeyboardEvent>();
	private readonly textInputFocusContext: IContextKey<boolean>;

	constructor(
		@ICommandService private readonly commandService: ICommandService,
		@IContextKeyService private readonly contextService: IContextKeyService,
		@IKeyboardService private readonly keyboardService: IKeyboardService,
		@ILogService private readonly logger: ILogService,
		@IUserConfiguration private readonly config: IUserConfiguration,
	) {
		super();
		this.textInputFocusContext = this.contextService.createKey("textInputFocus", false);
		this.registerGlobalKeyboardListener();
	}

	lookupKeybinding(commandId: string, context?: IContextKeyService, enforceContextCheck?: boolean): IResolvedKeybinding | undefined {
		const candidates = Array.from(this.keybindings.values()).filter(kb => kb.commandId === commandId);

		if (candidates.length === 0) {
			return undefined;
		}

		// If context is provided, filter by "when" clause
		if (context) {
			for (const candidate of candidates) {
				if (!candidate.when) {
				}
			}
		}

		const first = candidates[0];
		const key = Array.isArray(first.key) ? first.key[0] : first.key;
		return {
			getLabel: () => key
		}
	}


	private registerGlobalKeyboardListener(): void {
		this._register(this.keyboardService.onKeyDown(this.handleKeyboardEvent));
		// canvas shortcuts (chrome extension): a second, UNSCOPED subscription for
		// bindings flagged `allowOnCanvas` — the scoped stream is silent while the
		// host page / its canvas has focus, so undo or W/E after a gizmo drag went
		// nowhere until the user refocused an editor panel. In-scope events are
		// skipped here (the scoped handler above owns them); in products without
		// scoping (pwa) every event is in scope, so this path is inert.
		this._register(this.keyboardService.onKeyDown(this.handleCanvasKeyboardEvent, { scoped: false }));
	}

	// takes the INTERFACE the keyboard service emits, not the concrete class —
	// StandardKeyboardEvent has private members, so a class-typed handler can
	// never accept what the service contract promises
	private handleKeyboardEvent = (evt: IKeyboardEvent) => {
		const event = evt.browserEvent;
		if (event.defaultPrevented || this.handledEvents.has(event)) return;
		if (this.hasSelectedTextForNativeCopy(event)) return;
		const sourceDocument = (evt as IKeyboardEventWithSource).sourceWindow?.document;
		this.updateTextInputContext(sourceDocument);
		const binding = this.resolveKeybinding(event);

		if (!binding) {
			return; // No matching keybinding
		}

		// typing must win: a modifier-less binding (W/E, Delete) fired from
		// inside a text field would edit the scene while the user writes text
		if (!event.ctrlKey && !event.metaKey && !event.altKey && isEditableElementFocused(sourceDocument)) {
			return;
		}

		// Check if command can execute. NOTE the `.canExecute === false` shape:
		// canExecuteCommand returns an OBJECT, so a bare truthiness check never
		// blocked anything — load-bearing since the gizmo commands gate W/E on
		// "has target + no mouse button held" for the fly-key coexistence.
		const can = this.commandService.canExecuteCommand(binding.commandId, binding.args);
		if (can && can.canExecute === false) {
			return;
		}

		if (binding.preventDefault === true) {
			evt.preventDefault();
		}

		// Execute the command
		this.handledEvents.add(event);
		this.commandService.executeCommand(binding.commandId, binding.args, { isAi: false, keyboardEvent: evt }).catch(err => {
			this.logger.error(`Failed to execute command '${binding.commandId}' from keybinding:`, err);
		});
	}

	/**
	 * Keep the `textInputFocus` when-clause key current, evaluated per press
	 * rather than tracked on focus events — focus moves through shadow roots
	 * and popout windows, and a stale "true" would silently kill every guarded
	 * shortcut. The key has always been REFERENCED (dock maximize/minimize,
	 * duplicate) and never set, so `!textInputFocus` was true forever.
	 *
	 * It exists for MODIFIER bindings: the guard below covers modifier-less
	 * ones wholesale, but a Ctrl combo must be judged per binding — Ctrl+Z has
	 * to keep working while typing, Ctrl+D must not (in Monaco it means
	 * "add selection to next match").
	 */
	private updateTextInputContext(sourceDocument?: Document): void {
		this.textInputFocusContext.set(isEditableElementFocused(sourceDocument));
	}

	private hasSelectedTextForNativeCopy(event: KeyboardEvent): boolean {
		if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "c") return false;
		const target = event.composedPath?.()[0] ?? event.target;
		if (!target || !("getRootNode" in target)) return false;
		const selection = selectionForElement(target as Node);
		return !!selection && !selection.isCollapsed && selection.toString().length > 0;
	}

	/** Unscoped delivery for `allowOnCanvas` bindings — the host page has focus. */
	private handleCanvasKeyboardEvent = (evt: IKeyboardEvent) => {
		const event = evt.browserEvent;
		if (event.defaultPrevented || this.handledEvents.has(event)) return;
		if (this.hasSelectedTextForNativeCopy(event)) return;
		// in-scope events are the scoped handler's job (avoid double-execute)
		if (this.keyboardService.isEventInScope(event)) return;
		if (!this.config.canvasShortcuts) return;
		// typing into a host input must win over any shortcut
		const sourceDocument = (evt as IKeyboardEventWithSource).sourceWindow?.document;
		if (isEditableElementFocused(sourceDocument)) return;
		this.updateTextInputContext(sourceDocument);

		const binding = this.resolveKeybinding(event);
		if (!binding || binding.allowOnCanvas !== true) return;

		// canExecute gating doubles as the collision guard: W/E only bind while a
		// gizmo target is selected, undo only while the stack has entries — a key
		// the editor cannot use RIGHT NOW is left to the host page untouched.
		const can = this.commandService.canExecuteCommand(binding.commandId, binding.args);
		if (can && can.canExecute === false) return;

		// ours now: the same press must not ALSO drive the host page (W switching
		// to move mode must not walk the site's character)
		evt.preventDefault();
		evt.stopPropagation();
		this.handledEvents.add(event);

		this.commandService.executeCommand(binding.commandId, binding.args, { isAi: false, keyboardEvent: evt }).catch(err => {
			this.logger.error(`Failed to execute command '${binding.commandId}' from canvas keybinding:`, err);
		});
	}

	registerKeybinding(binding: IKeybinding): IDisposable {
		if (this.keybindings.has(binding.id)) {
			this.logger.warn(`Keybinding with id '${binding.id}' already exists, overwriting`);
		}

		this.keybindings.set(binding.id, binding);
		this._onDidUpdateKeybindings.fire();

		return {
			dispose: () => {
				this.keybindings.delete(binding.id);
				this._onDidUpdateKeybindings.fire();
			}
		};
	}

	createContextKey<T extends ContextKeyValue>(key: string, defaultValue: T): IContextKey<T> {
		return this.contextService.createKey(key, defaultValue);
	}

	contextMatches(when: string): boolean {
		const rule = ContextKeyExpr.deserialize(when);
		return !!rule && this.contextService.contextMatchesRules(rule);
	}

	getKeybindings(): readonly IKeybinding[] {
		return Array.from(this.keybindings.values());
	}

	getKeybindingsForCommand(commandId: string): readonly IKeybinding[] {
		return Array.from(this.keybindings.values()).filter(kb => kb.commandId === commandId);
	}

	resolveKeybinding(event: KeyboardEvent): IKeybinding | undefined {
		// Build the key combo string from the event
		const keyCombo = this.buildKeyCombo(event);

		// Find matching keybindings
		const candidates = Array.from(this.keybindings.values()).filter(kb => {
			const codes = kb.code ? (Array.isArray(kb.code) ? kb.code : [kb.code]) : [];
			if (codes.includes(event.code)) return true;
			if (Array.isArray(kb.key)) {
				return kb.key.some(key => this.normalizeKeyCombo(key) === keyCombo);
			} else {
				return this.normalizeKeyCombo(kb.key) === keyCombo;
			}
		});

		if (candidates.length === 0) {
			return undefined;
		}

		// Later registrations are the active scope's overrides; base bindings remain fallbacks.
		for (let index = candidates.length - 1; index >= 0; index--) {
			const candidate = candidates[index];
			const rule = candidate.when ? ContextKeyExpr.deserialize(candidate.when) : undefined;
			if (!candidate.when || (rule && this.contextService.contextMatchesRules(rule))) {
				return candidate;
			}
		}

		return undefined;
	}

	/**
	 * Build a normalized key combo string from a keyboard event.
	 * Examples: "ctrl+z", "shift+alt+f", "delete", "escape"
	 */
	private buildKeyCombo(event: KeyboardEvent): string {
		const parts: string[] = [];

		// Add modifiers in consistent order
		if (event.ctrlKey || event.metaKey) parts.push('ctrl'); // Treat Cmd as Ctrl
		if (event.altKey) parts.push('alt');
		if (event.shiftKey) parts.push('shift');

		// Add the main key
		const key = this.normalizeKey(event.key);
		if (key) {
			parts.push(key);
		}

		return parts.join('+');
	}

	/**
	 * Normalize a key string to lowercase and handle special cases
	 */
	private normalizeKey(key: string): string {
		// Handle special keys
		const normalized = key?.toLowerCase();

		// Map some keys to more consistent names
		const keyMap: Record<string, string> = {
			'control': 'ctrl',
			'command': 'ctrl',
			'meta': 'ctrl',
			'option': 'alt',
			' ': 'space',
		};

		return keyMap[normalized] || normalized;
	}

	/**
	 * Normalize a key combo string (e.g., "Ctrl+Z" -> "ctrl+z")
	 */
	private normalizeKeyCombo(keyCombo: string): string {
		const parts = keyCombo.toLowerCase().split('+').map(p => p.trim());

		// Sort modifiers consistently
		const modifiers: string[] = [];
		let mainKey = '';

		for (const part of parts) {
			const normalized = this.normalizeKey(part);
			if (normalized === 'ctrl' || normalized === 'alt' || normalized === 'shift') {
				if (!modifiers.includes(normalized)) {
					modifiers.push(normalized);
				}
			} else {
				mainKey = normalized;
			}
		}

		// Sort modifiers: ctrl, alt, shift
		modifiers.sort((a, b) => {
			const order = ['ctrl', 'alt', 'shift'];
			return order.indexOf(a) - order.indexOf(b);
		});

		if (mainKey) {
			modifiers.push(mainKey);
		}

		return modifiers.join('+');
	}
}

registerSingleton(IKeybindingService, new SyncDescriptor(KeybindingService));
